import { Divider, Flex, HStack, Text } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { ReactComponent as LeftIcon } from "../../assets/chevron-left.svg";
import { ReactComponent as RightIcon } from "../../assets/chevron-right.svg";
import { useLocation, useNavigate } from "react-router-dom";
import useLegacyIframeResizeMessage from "../../hooks/useLegacyIframeResizeMessage";
import userValidationSchema from "../../validation/userValidation";
import dayLabels from "../../data/dayLabels.json";
import CustomButton from "../../Components/Button/CustomButton";
import Calendar from "../../Components/Calendar/calendar";
import Heading from "../../Components/Text/heading";
import SubHeading from "../../Components/Text/subHeading";
import SmallFlexContainer from "../../Components/Container/smallFlexContainer";
import LargeContainer from "../../Components/Container/largeContainer";
import Slots from "../../Components/Slots/Slots";
import {
  addBooking,
  getMonthlyData,
  updateBooking,
} from "../../bookingapi/booking.operations";
import { getDailyRecords } from "../../utils/DailyRecords";
import Form from "../../Components/Forms/Form";
import LoadingScreen from "../../Components/LoadingScreen/LoadingScreen";

export const validateMaxDate = (maxDate) => {
  if (maxDate === undefined) return true;
  if (maxDate === null) return true;

  if (maxDate.getFullYear() === 2099) return true;

  return false;
};

export default function AddBooking() {
  const location = useLocation();
  const navigate = useNavigate();
  useLegacyIframeResizeMessage();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");
  const today = new Date();

  const [calendarDate, setCalendarDate] = useState(today);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [monthlyData, setMonthlyData] = useState([]);
  const [dailyData, setDailyData] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState({});
  const [errors, setErrors] = useState({});
  const [loader, setLoader] = useState(true);
  const year = calendarDate.getFullYear();
  const month = calendarDate.getMonth() + 1;

  const {
    customerId = "",
    customerFirstName = "",
    customerLastName = "",
    phoneNumber = "",
    maxDate,
    overwriteExistingBooking,
    bookingId,
    existingBookingToken,
  } = location?.state || {};

  const invalidMaxDate = validateMaxDate(maxDate);

  useEffect(() => {
    const monthlyData = async () => {
      try {
        const data = await getMonthlyData(year, month);
        setMonthlyData(data);
      } catch (err) {
        console.log(err);
      } finally {
        setLoader(false);
      }
    };

    monthlyData();
  }, [calendarDate, month, year]);

  useEffect(() => {
    if (selectedDate) {
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth() + 1; // Add 1 to get the month number starting from 1 instead of 0
      const day = selectedDate.getDate();

      getDailyRecords(day, month, year, setDailyData);
    }
  }, [selectedDate]);

  const handleSubmit = async (event) => {
    if (!selectedDate || selectedDate === "") {
      alert("Please select a date");
    }

    if (!selectedSlot || selectedSlot === "") {
      alert("Please select a slot");
    }

    if (overwriteExistingBooking) {
      try {
        let body = {
          timeSlotId: selectedSlot.timeSlotId,
          year: selectedDate.getFullYear(),
          month: selectedDate.getMonth() + 1,
          day: selectedDate.getDate(),
          token: token || existingBookingToken,
        };
        await updateBooking(bookingId, body);
        navigate("/booking-confirmed", {
          state: { selectedSlot, selectedDate },
        });
        setLoading(false);
        return;
      } catch (error) {
        console.error(error);
        alert("Something went wrong while updating the booking");
        setLoading(false);
        return;
      }
    }

    try {
      let date = `${selectedDate.getFullYear()}-${(selectedDate.getMonth() + 1)
        .toString()
        .padStart(2, "0")}-${selectedDate
        .getDate()
        .toString()
        .padStart(2, "0")}`;

      let body = {
        bookingDate: date,
        bookingTimeSlot: selectedSlot.timeSlotId,
        status: "BOOKED",
        customerId: customerId.toString().trim(),
        customerFirstName: customerFirstName,
        customerLastName: customerLastName,
        phoneNumber: phoneNumber,
      };

      const response = await addBooking(body);

      if (response.status === "success") {
        navigate("/booking-confirmed", {
          state: {
            showAmendButton: true,
            selectedSlot,
            maxDate,
            selectedDate,
            ...body,
          },
          replace: true,
        });
        setLoading(false);
        setSelectedSlot("");
        // Fire the event to parent window after the booking has been completed
        if (window.parent) {
          window.parent.postMessage(
            {
              type: "BOOKING_SYSTEM_EVENT",
              data: "COMPLETE",
            },
            "*"
          );
        }
      } else {
        if (response.data === "Slot not found") {
          alert("Slot not found");
          return;
        } else if (response?.data?.reason === "Booking already found") {
          alert("Booking already found");
          return;
        } else if (
          response?.data?.reason === "Booking not available for this date"
        ) {
          alert("Booking not available for this date");
          return;
        } else {
          alert("Something went wrong");
          return;
        }
      }
    } catch (error) {
      console.error(error);
      alert("Something went wrong");
      setLoading(false);
    }
  };

  function makeBooking(event) {
    event.preventDefault();
    setLoading(true);
    userValidationSchema
      .validate(
        { customerFirstName, customerLastName, phoneNumber, customerId },
        { abortEarly: false }
      )
      .then(() => {
        // Validation successful, submit the form data
        handleSubmit();
      })
      .catch((validationErrors) => {
        // Validation failed, set the errors state variable
        const errors = {};
        validationErrors.inner.forEach((error) => {
          errors[error.path] = error.message;
        });
        setErrors(errors);
        setLoading(false);
      });
  }

  const btnRef = React.useRef();

  function subtractMonth() {
    setCalendarDate(
      new Date(calendarDate.setMonth(calendarDate.getMonth() - 1))
    );
  }
  function addMonth() {
    setCalendarDate(
      new Date(calendarDate.setMonth(calendarDate.getMonth() + 1))
    );
  }

  function getAvailableSlotsForDay(day) {
    // Find the corresponding day data in the monthlyData array
    const dayData = monthlyData?.find((d) => d.date[2] === day);
    // If dayData exists, return the availableSlots, otherwise return 0
    const slots = dayData ? dayData?.availableSlots - dayData?.bookedSlots : 0;
    // given customer visibile app, mask possible negative slots.
    return slots > 0 ? slots : 0;
  }

  function renderDays() {
    // Get today's date
    let today = new Date();
    // Make sure to set the time to midnight for a fair comparison
    today.setHours(0, 0, 0, 0);

    return (
      <>
        {loader ? (
          <LoadingScreen
            loadingText={"Hold tight"}
            loadingSubText={"It will just take a few moments"}
          />
        ) : (
          <Calendar
            getAvailableSlotsForDay={getAvailableSlotsForDay}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            calendarDate={calendarDate}
          />
        )}
      </>
    );
  }

  const formattedDate = ` ${calendarDate.toLocaleString("default", {
    month: "long",
  })} ${calendarDate.getFullYear()}`;

  return (
    <LargeContainer minH={"100vh"}>
      <SmallFlexContainer px={{ base: "5%", md: "20%" }}>
        <SmallFlexContainer
          py={"30px"}
          minHeight="714px"
          justifyContent="space-between"
          flexDirection={{ base: "column", md: "row" }}
        >
          <SmallFlexContainer
            width={{ base: "100%", md: "49.14%" }}
            backgroundColor={"#FFFFFF"}
            alignItems={"center"}
          >
            <Form
              errors={errors}
              formData={{
                customerId,
                customerFirstName,
                customerLastName,
                phoneNumber,
              }}
              disabled
            />

            {invalidMaxDate ? (
              <SmallFlexContainer alignItems={"center"} marginTop="60px">
                <Text>No Dates Available!</Text>
              </SmallFlexContainer>
            ) : (
              <Flex
                width={"92.92%"}
                justifyContent={"center"}
                minheight={"384px"}
              >
                <Flex width={"100%"} flexDirection={"column"}>
                  <Flex width={"100%"} minheight={"80px"}>
                    <Flex marginTop={"27px"}>
                      <LeftIcon
                        style={{
                          cursor: "pointer",
                        }}
                        onClick={subtractMonth}
                      />
                      &nbsp;
                      <RightIcon
                        style={{
                          cursor: "pointer",
                        }}
                        onClick={addMonth}
                      />
                    </Flex>
                    <Heading headingLabel={formattedDate} />
                  </Flex>
                  <HStack spacing="1.26%" marginTop={"16px"}>
                    {dayLabels.map((day, index) => (
                      <SubHeading
                        key={index}
                        textAlign={"center"}
                        w="13.2%"
                        h="16px"
                        subHeadingLabel={day.abbreviation}
                      ></SubHeading>
                    ))}
                  </HStack>
                  <Flex marginTop={"8px"} marginBottom="16px">
                    {renderDays()}
                  </Flex>
                </Flex>
              </Flex>
            )}
          </SmallFlexContainer>

          <SmallFlexContainer
            width={{ base: "100%", md: "49.14%" }}
            backgroundColor={"#FFFFFF"}
            alignItems={"center"}
            mt={{ base: "20px", md: "0px" }}
          >
            <SmallFlexContainer width={"92.92%"}>
              <Heading headingLabel="Choose your time slot" />
              {invalidMaxDate ? (
                <SmallFlexContainer alignItems="center" marginTop={"30px"}>
                  <Text>No Time Slots Available!</Text>
                </SmallFlexContainer>
              ) : (
                <Slots
                  dailyData={dailyData}
                  selectedSlot={selectedSlot}
                  setSelectedSlot={setSelectedSlot}
                  selectedDate={selectedDate}
                ></Slots>
              )}
            </SmallFlexContainer>
            <SmallFlexContainer minHeight={"104px"}>
              <Divider />

              {!invalidMaxDate && selectedDate && (
                <CustomButton
                  ref={btnRef}
                  onClick={makeBooking}
                  marginTop={"32px"}
                  buttonLabel="Confirm Booking"
                  isDisabled={loading}
                  isLoading={loading}
                ></CustomButton>
              )}
            </SmallFlexContainer>
          </SmallFlexContainer>
        </SmallFlexContainer>
      </SmallFlexContainer>
    </LargeContainer>
  );
}
